import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';

import { FeatureFlag } from 'constants/featureFlags';
import { CreditReportField, getRunAusOptions } from 'constants/loanActionEditor';
import { PAGE_CODES } from 'constants/pageCodes';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  selectDynamicFieldsOptions,
  selectExtendedFormBundlesOptions,
  selectFormsInfoListByGroupNameOptions,
} from 'store/forms/selectors';
import { selectSecurityGroupOptions } from 'store/global/selectors';
import { selectHalcyonFormPickerOptions } from 'store/loanActions/halcyonFormCatalog';
import { selectFloodProductOptions } from 'store/loanActions/servicelinkFloodProducts';
import {
  selectActiveDocumentTypesOptions,
  selectChecklistsOptions,
  selectCustomFields,
  selectEmailTemplatesArray,
  selectExistingImpoundTypesOptions,
  selectExtendedLoanActionFieldOptions,
  selectHalcyonFormInclusionSchema,
  selectImpoundTypesOptions,
  selectLoanActionArithmeticOperationsSchema,
  selectLoanActionCriteriaSchema,
  selectLoanActionFieldOptions,
  selectLoanActionFieldsMap,
  selectLoanEditorCustomForms,
  selectLoanRoleAssignmentCriteriaSchema,
  selectLoanRolesOptions,
  selectReportsOptions,
  selectSystemTagsOptions,
  selectTaskTypes,
  selectTaskTypesOptions,
} from 'store/system/selectors';
import { selectUsersOptions } from 'store/users/selectors';

import { buildCustomFieldDefinitionOptions } from '../LoanActionEnterCustomFieldsRow';
import { getLoanEditorActionTypeOptions } from '../LoanActionOptions.utils';

/**
 * Collects every option list, schema and lookup map the manual loan-action item editor and its
 * API <-> form transformations need, so Loan Actions and Task Type actions share one source.
 * @param {{ targetType: string }} params
 */
const useLoanActionItemsOptions = ({ targetType }) => {
  const isEnabledAusPendingChanges = useFeatureToggle(FeatureFlag.EnableAusPendingChanges);
  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );
  const isSavePendingChangesLoanActionEnabled = useFeatureToggle(
    FeatureFlag.EnableSavePendingChangesLoanAction,
  );

  const loanActionFieldsMap = useSelector(selectLoanActionFieldsMap);
  const schemaParams = useSelector(selectLoanActionCriteriaSchema);
  const arithmeticOperationsSchemaParams = useSelector(selectLoanActionArithmeticOperationsSchema);
  const halcyonExpressionSchemaParams = useSelector(selectHalcyonFormInclusionSchema);
  const halcyonFormOptions = useSelector(selectHalcyonFormPickerOptions);
  const loanRoleCriteriaSchemaParams = useSelector(selectLoanRoleAssignmentCriteriaSchema);
  const tagsOptions = useSelector(selectSystemTagsOptions({ withDeleted: true }));
  const customFieldDefinitions = useSelector(selectCustomFields);
  const fieldsOptions = useSelector(selectLoanActionFieldOptions);
  const extendedFieldsOptions = useSelector(selectExtendedLoanActionFieldOptions);
  const formsOptions = useSelector(selectFormsInfoListByGroupNameOptions);
  const formBundlesOptions = useSelector(selectExtendedFormBundlesOptions);
  const documentTypesOptions = useSelector(selectActiveDocumentTypesOptions);
  const checklistsOptions = useSelector(selectChecklistsOptions);
  const reportsOptions = useSelector(selectReportsOptions);
  const impoundTypeOptions = useSelector(selectExistingImpoundTypesOptions);
  const allImpoundTypeOptions = useSelector(selectImpoundTypesOptions);
  const taskTypes = useSelector(selectTaskTypes);
  const taskTypesOptions = useSelector(selectTaskTypesOptions);
  const floodProductOptions = useSelector(selectFloodProductOptions);
  const loanRoleOptions = useSelector(selectLoanRolesOptions);
  const securityGroupOptions = useSelector(selectSecurityGroupOptions);
  const dynamicFieldsOptions = useSelector(selectDynamicFieldsOptions);
  const emailTemplatesOptions = useSelector(selectEmailTemplatesArray);
  const usersOptions = useSelector(selectUsersOptions(null));
  const loanEditorCustomForms = useSelector(selectLoanEditorCustomForms);
  const { isViewAllowed: isCustomFormsViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CUSTOM_FORMS),
  );

  const customFieldDefinitionOptions = useMemo(
    () => buildCustomFieldDefinitionOptions(customFieldDefinitions),
    [customFieldDefinitions],
  );

  const loanEditorCustomFormsOptions = useMemo(
    () =>
      loanEditorCustomForms
        .filter(({ isEnabled }) => isEnabled)
        .map(({ id, name }) => ({ value: id, name })),
    [loanEditorCustomForms],
  );

  const extendedReportsOptions = useMemo(
    () => [...reportsOptions, CreditReportField],
    [reportsOptions],
  );

  const runAusOptions = useMemo(
    () => getRunAusOptions({ isEnabledAusPendingChanges }),
    [isEnabledAusPendingChanges],
  );

  const actionTypeOptions = useMemo(
    () =>
      getLoanEditorActionTypeOptions({
        targetType,
        isSavePendingChangesLoanActionEnabled,
        isCustomFormsViewAllowed,
      }),
    [targetType, isSavePendingChangesLoanActionEnabled, isCustomFormsViewAllowed],
  );

  return useMemo(
    () => ({
      isNewExpressionsParsingLogicEnabled,
      isCustomFormsViewAllowed,
      loanActionFieldsMap,
      schemaParams,
      arithmeticOperationsSchemaParams,
      halcyonExpressionSchemaParams,
      halcyonFormOptions,
      loanRoleCriteriaSchemaParams,
      tagsOptions,
      customFieldDefinitionOptions,
      fieldsOptions,
      extendedFieldsOptions,
      formsOptions,
      formBundlesOptions,
      documentTypesOptions,
      checklistsOptions,
      extendedReportsOptions,
      impoundTypeOptions,
      allImpoundTypeOptions,
      taskTypes,
      taskTypesOptions,
      floodProductOptions,
      loanRoleOptions,
      securityGroupOptions,
      dynamicFieldsOptions,
      emailTemplatesOptions,
      usersOptions,
      loanEditorCustomFormsOptions,
      runAusOptions,
      actionTypeOptions,
    }),
    [
      isNewExpressionsParsingLogicEnabled,
      isCustomFormsViewAllowed,
      loanActionFieldsMap,
      schemaParams,
      arithmeticOperationsSchemaParams,
      halcyonExpressionSchemaParams,
      halcyonFormOptions,
      loanRoleCriteriaSchemaParams,
      tagsOptions,
      customFieldDefinitionOptions,
      fieldsOptions,
      extendedFieldsOptions,
      formsOptions,
      formBundlesOptions,
      documentTypesOptions,
      checklistsOptions,
      extendedReportsOptions,
      impoundTypeOptions,
      allImpoundTypeOptions,
      taskTypes,
      taskTypesOptions,
      floodProductOptions,
      loanRoleOptions,
      securityGroupOptions,
      dynamicFieldsOptions,
      emailTemplatesOptions,
      usersOptions,
      loanEditorCustomFormsOptions,
      runAusOptions,
      actionTypeOptions,
    ],
  );
};

export default useLoanActionItemsOptions;
